/**
 * The row actions menu for admin CRM tables.
 *
 * Every table had its own row of coloured buttons, which grew with each new
 * capability until the actions column was wider than the data it belonged to and
 * every table had drifted to a slightly different set of paddings and tones.
 * One trigger per row, one menu behind it, one implementation.
 *
 * Accessibility is the reason this is a component rather than a styled div: a
 * menu has obligations — it closes on Escape and on outside click, it returns
 * focus to its trigger, it moves through items with the arrow keys, and it
 * reports its state to assistive technology. Those are easy to get wrong once
 * per table and easy to get right once here.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

export type DataTableAction = {
  /** Stable identity for the item, used as the React key. */
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  /** Native tooltip and, when the item is disabled, the reason it is. */
  title?: string;
  /**
   * `danger` for anything that revokes or destroys. Colour is not the only
   * signal — these items also sit last, behind a divider — but an administrator
   * scanning a menu should be able to see the irreversible one at a glance.
   */
  tone?: "default" | "danger";
};

export function DataTableActions({
  actions,
  label = "Actions",
  subject,
  align = "end",
}: {
  actions: DataTableAction[];
  label?: string;
  /** Who the row is about, so each trigger has a distinct accessible name. */
  subject?: string;
  align?: "start" | "end";
}) {
  const [open, setOpen] = useState(false);
  /*
   * The menu is rendered in a portal at a fixed position rather than absolutely
   * inside the row. Every CRM table scrolls horizontally, and `overflow-x: auto`
   * clips vertically too — so a menu opened on the last row was cut off by the
   * table's own scroll container. A portal escapes that entirely, and also puts
   * the menu above any stacking context the table sets up.
   */
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = `row-actions-${useId().replace(/:/g, "")}`;

  const usable = actions.filter(action => !action.disabled);

  const MENU_WIDTH = 240;

  useLayoutEffect(() => {
    if (!open) return;

    const place = () => {
      const trigger = triggerRef.current?.getBoundingClientRect();
      const menu = menuRef.current?.getBoundingClientRect();
      if (!trigger) return;

      const height = menu?.height ?? 0;
      const spaceBelow = window.innerHeight - trigger.bottom;
      // Flip above the trigger when the row is near the bottom of the window.
      const top = height && spaceBelow < height + 12
        ? Math.max(8, trigger.top - height - 6)
        : trigger.bottom + 6;

      const rawLeft = align === "end" ? trigger.right - MENU_WIDTH : trigger.left;
      const left = Math.min(Math.max(8, rawLeft), window.innerWidth - MENU_WIDTH - 8);
      setPosition({ top, left });
    };

    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, align, actions.length]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      // Focus goes back to the trigger, not to the top of the document — an
      // administrator who dismisses a menu is still working on that row.
      triggerRef.current?.focus();
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const focusItem = (index: number) => {
    const items = menuRef.current?.querySelectorAll<HTMLButtonElement>("[data-row-action]");
    if (!items?.length) return;
    const wrapped = ((index % items.length) + items.length) % items.length;
    items[wrapped]?.focus();
  };

  const onMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>("[data-row-action]") ?? []);
    const current = items.findIndex(item => item === document.activeElement);
    if (event.key === "Home") return focusItem(0);
    if (event.key === "End") return focusItem(items.length - 1);
    focusItem(current + (event.key === "ArrowDown" ? 1 : -1));
  };

  if (actions.length === 0) return null;

  return (
    <div ref={rootRef} className="crm-row-actions" style={{ position: "relative", display: "inline-block" }}>
      <button
        ref={triggerRef}
        type="button"
        className="crm-row-actions__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={subject ? `${label} for ${subject}` : undefined}
        disabled={usable.length === 0}
        title={usable.length === 0 ? "No actions available for this row" : undefined}
        onClick={() => setOpen(current => !current)}
        onKeyDown={event => {
          if (event.key === "ArrowDown" && !open) {
            event.preventDefault();
            setOpen(true);
            window.requestAnimationFrame(() => focusItem(0));
          }
        }}
      >
        {label}
        <ChevronDown size={12} aria-hidden="true" data-open={open || undefined} />
      </button>

      {open && createPortal(
        <div
          id={menuId}
          ref={menuRef}
          role="menu"
          className="crm-row-actions__menu"
          onKeyDown={onMenuKeyDown}
          style={{ top: position?.top ?? -9999, left: position?.left ?? -9999, width: MENU_WIDTH }}
        >
          {actions.map((action, index) => {
            const previous = actions[index - 1];
            const startsDangerGroup = action.tone === "danger" && previous && previous.tone !== "danger";
            return (
              <button
                key={action.key}
                type="button"
                role="menuitem"
                data-row-action
                data-tone={action.tone === "danger" ? "danger" : undefined}
                data-divider={startsDangerGroup || undefined}
                disabled={action.disabled}
                title={action.title}
                onClick={() => {
                  // Closed before the handler runs, so an action that opens a
                  // dialog does not leave the menu hanging behind it.
                  setOpen(false);
                  action.onSelect();
                }}
              >
                {action.icon}
                <span>{action.label}</span>
              </button>
            );
          })}
        </div>,
        document.body,
      )}

      <style>{`
        .crm-row-actions__trigger {
          display: inline-flex;
          align-items: center;
          gap: .35rem;
          min-height: 44px;
          padding: 0 .75rem;
          border: 1px solid rgba(255,255,255,.14);
          border-radius: .45rem;
          background: rgba(255,255,255,.05);
          color: #CBD5E1;
          font-family: 'Space Grotesk', sans-serif;
          font-size: .84rem;
          font-weight: 700;
          cursor: pointer;
          transition: border-color .15s ease, background .15s ease, color .15s ease;
        }

        .crm-row-actions__trigger:hover:not(:disabled) {
          border-color: rgba(0,212,255,.4);
          background: rgba(0,212,255,.1);
          color: #7DEAFF;
        }

        .crm-row-actions__trigger:disabled { opacity: .45; cursor: not-allowed; }

        .crm-row-actions__trigger svg { transition: transform .16s ease; }
        .crm-row-actions__trigger svg[data-open] { transform: rotate(180deg); }

        .crm-row-actions__menu {
          position: fixed;
          z-index: 120;
          display: flex;
          flex-direction: column;
          padding: .3rem;
          border: 1px solid rgba(255,255,255,.12);
          border-radius: .6rem;
          background: #0D1220;
          box-shadow: 0 18px 44px rgba(0,0,0,.5);
        }

        .crm-row-actions__menu button {
          display: flex;
          align-items: center;
          gap: .45rem;
          width: 100%;
          min-height: 44px;
          padding: .55rem .65rem;
          border: 0;
          border-radius: .4rem;
          background: none;
          color: #CBD5E1;
          font-family: 'Space Grotesk', sans-serif;
          font-size: .86rem;
          font-weight: 650;
          text-align: left;
          white-space: nowrap;
          cursor: pointer;
        }

        .crm-row-actions__menu button:hover:not(:disabled),
        .crm-row-actions__menu button:focus-visible {
          background: rgba(255,255,255,.07);
          color: #F8FAFC;
          outline: none;
        }

        .crm-row-actions__menu button:focus-visible {
          box-shadow: inset 0 0 0 1px rgba(0,212,255,.5);
        }

        .crm-row-actions__menu button:disabled { opacity: .42; cursor: not-allowed; }

        .crm-row-actions__menu button[data-tone="danger"] { color: #F87171; }
        .crm-row-actions__menu button[data-tone="danger"]:hover:not(:disabled) {
          background: rgba(239,68,68,.12);
          color: #FCA5A5;
        }

        /* The irreversible group is set apart, not only recoloured. */
        .crm-row-actions__menu button[data-divider] {
          margin-top: .3rem;
          padding-top: .55rem;
          border-top: 1px solid rgba(255,255,255,.09);
          border-radius: 0 0 .4rem .4rem;
        }

        @media (prefers-reduced-motion: reduce) {
          .crm-row-actions__trigger,
          .crm-row-actions__trigger svg { transition: none; }
        }
      `}</style>
    </div>
  );
}

export default DataTableActions;
